import { useEffect } from 'react';
import {
  getNewsArticles,
  getNewsCategories,
  getNewsSubcategories,
  isNewsAtiva,
  mergeNewsArticleSubcategoryIds,
  type NewsArticle,
  type NewsLocalizacaoEscopo,
} from '../lib/newsStorage';
import { getCidadeById } from '../lib/experienciasStorage';
import { getEstabelecimentoById, mergeEstabelecimentoSubcategoryIds } from '../lib/estabelecimentosStorage';
import { getEstabelecimentoCategories, getEstabelecimentoSubcategories } from '../lib/estabelecimentosCategoriasStorage';
import { getAgenciaById, getGuiaById } from '../lib/guiasAgenciasStorage';

const MESSAGE_TYPE = 'naviex-noticias-export';

function locExportParaCliente(a: NewsArticle): {
  cityId: string;
  cityName: string;
  regionName: string;
  localizacaoEscopo: NewsLocalizacaoEscopo;
} {
  const esc: NewsLocalizacaoEscopo = a.localizacaoEscopo ?? (a.cityId ? 'cidade' : 'cidade');
  if (esc === 'global') {
    return { cityId: '', cityName: 'Global', regionName: '', localizacaoEscopo: 'global' };
  }
  if (esc === 'pais') {
    const n = a.localizacaoPais?.trim() || '';
    return { cityId: '', cityName: n || 'País', regionName: '', localizacaoEscopo: 'pais' };
  }
  if (esc === 'estado') {
    const cityName = [a.localizacaoEstado, a.localizacaoPais].filter(Boolean).join(', ');
    return { cityId: '', cityName: cityName || 'Estado', regionName: '', localizacaoEscopo: 'estado' };
  }
  if (esc === 'regiao') {
    const cityName = [a.localizacaoRegiao, a.localizacaoEstado, a.localizacaoPais].filter(Boolean).join(' — ');
    return {
      cityId: '',
      cityName: cityName || 'Região',
      regionName: a.localizacaoRegiao?.trim() || '',
      localizacaoEscopo: 'regiao',
    };
  }
  const city = getCidadeById(a.cityId);
  const cityName = city ? [city.cidade, city.estadoProvincia].filter(Boolean).join(', ') : '';
  const regionName = city?.regioes?.[0] ?? '';
  return { cityId: a.cityId, cityName, regionName, localizacaoEscopo: 'cidade' };
}

/**
 * Página pública (sem login) usada pelo cliente em iframe para obter notícias
 * quando cliente e admin rodam em origens diferentes (ex.: admin:3007, cliente:3000).
 * Envia os dados via postMessage para window.parent.
 */
export default function NoticiasExportPage() {
  useEffect(() => {
    try {
      const allArticles = getNewsArticles();
      const articles = allArticles.filter(
        (a) =>
          a.publicacaoStatus !== 'rascunho' &&
          a.publicacaoStatus !== 'lixeira' &&
          isNewsAtiva(a)
      );
      const categories = getNewsCategories();
      const subcategories = getNewsSubcategories();
      const estCategories = getEstabelecimentoCategories();
      const estSubcategories = getEstabelecimentoSubcategories();

      const categoryById = new Map(categories.map((c) => [c.id, c.name]));
      const subcategoryById = new Map(subcategories.map((s) => [s.id, s.name]));
      const estCategoryById = new Map(estCategories.map((c) => [c.id, c.name]));
      const estSubcategoryById = new Map(estSubcategories.map((s) => [s.id, s.name]));

      const payload = {
        type: MESSAGE_TYPE,
        articles: articles.map((a) => {
          const { cityId, cityName, regionName, localizacaoEscopo } = locExportParaCliente(a);
          const estabelecimento = a.estabelecimentoId ? getEstabelecimentoById(a.estabelecimentoId) : null;
          const parceiroGuia = a.parceiroGuiaId ? getGuiaById(a.parceiroGuiaId) : null;
          const parceiroAgencia = a.parceiroAgenciaId ? getAgenciaById(a.parceiroAgenciaId) : null;
          const parceiroNome = parceiroGuia?.nome ?? parceiroAgencia?.razaoSocial ?? '';
          const parceiroTipo = parceiroGuia ? ('guia' as const) : parceiroAgencia ? ('agencia' as const) : null;
          const subIds = mergeNewsArticleSubcategoryIds(a);
          const subNames = subIds.map((id) => subcategoryById.get(id) ?? '').filter(Boolean);
          return {
            id: a.id,
            title: a.title,
            subtitle: a.subtitle,
            summary: a.summary,
            content: a.content,
            categoryId: a.categoryId,
            categoryName: categoryById.get(a.categoryId) ?? 'Notícia',
            subcategoryIds: subIds,
            subcategoryId: subIds[0] ?? '',
            subcategoryName: subNames.join(', '),
            cityId,
            cityName,
            regionName,
            localizacaoEscopo,
            coverImage: a.coverImage,
            images: a.images,
            layoutType: a.layoutType,
            layoutExpiresAt: a.layoutExpiresAt,
            destaqueSubcategoria: a.destaqueSubcategoria,
            destaqueSubcategoriaExpiresAt: a.destaqueSubcategoriaExpiresAt,
            destaqueNaviex: a.destaqueNaviex,
            destaqueNaviexExpiresAt: a.destaqueNaviexExpiresAt,
            tags: a.tags,
            responsibles: Array.isArray(a.responsibles) ? a.responsibles : [],
            estabelecimentoId: a.estabelecimentoId,
            parceiroGuiaId: a.parceiroGuiaId,
            parceiroAgenciaId: a.parceiroAgenciaId,
            parceiroTipo,
            parceiroNome: parceiroNome || undefined,
            estabelecimento: estabelecimento
              ? (() => {
                  const estSubIds = mergeEstabelecimentoSubcategoryIds(estabelecimento);
                  const estSubNames = estSubIds.map((id) => estSubcategoryById.get(id) ?? '').filter(Boolean);
                  return {
                  id: estabelecimento.id,
                  nome: estabelecimento.nome,
                  subtitulo: estabelecimento.subtitulo,
                  resumo: estabelecimento.resumo,
                  descricao: estabelecimento.descricao,
                  descontoParceriaDescricao: estabelecimento.descontoParceriaDescricao,
                  descontoMoradorAtivo: estabelecimento.descontoMoradorAtivo,
                  descontoMoradorModo: estabelecimento.descontoMoradorModo,
                  descontoMoradorValor: estabelecimento.descontoMoradorValor,
                  descontoMoradorTexto: estabelecimento.descontoMoradorTexto,
                  tags: estabelecimento.tags,
                  cnpj: estabelecimento.cnpj,
                  categoryId: estabelecimento.categoryId,
                  categoryName: estabelecimento.categoryId ? estCategoryById.get(estabelecimento.categoryId) ?? '' : '',
                  subcategoryIds: estSubIds,
                  subcategoryId: estSubIds[0] ?? '',
                  subcategoryName: estSubNames.join(', '),
                  responsaveis: estabelecimento.responsaveis,
                  horarioSemanal: estabelecimento.horarioSemanal,
                  excecoesCalendario: estabelecimento.excecoesCalendario,
                  };
                })()
              : null,
            createdAt: a.createdAt,
            updatedAt: a.updatedAt,
            statusPadrao: a.statusPadrao,
            periodosStatus: a.periodosStatus,
          };
        }),
        categories: categories.map((c) => ({ id: c.id, name: c.name })),
        subcategories,
      };

      if (window.parent !== window) {
        window.parent.postMessage(payload, '*');
      }
    } catch (err) {
      if (window.parent !== window) {
        window.parent.postMessage(
          { type: MESSAGE_TYPE, articles: [], categories: [], subcategories: [], error: String(err) },
          '*'
        );
      }
    }
  }, []);

  return (
    <div className="p-4 text-sm text-gray-500">
      Notícias enviadas ao cliente.
    </div>
  );
}
